/**
 * 用户状态管理模块
 * 参照xht-web项目的实现方式
 */
import { getUserInfo, getUserBalance } from '@/api/user'
import { getBusinessByUser } from '@/api/business'
import { saveLoginExpireTime, clearLoginExpireTime } from '@/utils/user'

const user = {
  state: {
    token: localStorage.getItem('token') || '',
    name: '',
    nickname: '',
    realName: '',
    avatar: '',
    phone: '',
    balance: 0,
    giftedBalance: 0,
    info: {}
  },

  mutations: {
    SET_TOKEN: (state, token) => {
      state.token = token
      localStorage.setItem('token', token)
      // 保存登录凭证过期时间（2天后）
      saveLoginExpireTime()
    },
    SET_NAME: (state, { name, nickname, realName }) => {
      state.name = name
      state.nickname = nickname
      state.realName = realName || ''
    },
    SET_AVATAR: (state, avatar) => {
      state.avatar = avatar
    },
    SET_PHONE: (state, phone) => {
      state.phone = phone
    },
    SET_BALANCE: (state, balance) => {
      state.balance = balance
    },
    SET_GIFTED_BALANCE: (state, giftedBalance) => {
      state.giftedBalance = giftedBalance
    },
    SET_INFO: (state, info) => {
      state.info = info
      // 如果用户信息中有wxOpenId，保存到localStorage
      if (info && info.wxOpenId) {
        localStorage.setItem('wx_open_id', info.wxOpenId)
      }
    },
    CLEAR_USER: (state) => {
      state.token = ''
      state.name = ''
      state.nickname = ''
      state.realName = ''
      state.avatar = ''
      state.phone = ''
      state.balance = 0
      state.giftedBalance = 0
      state.info = {}
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      // 清除登录凭证过期时间
      clearLoginExpireTime()
      // 清除openId（同时清除localStorage和sessionStorage中的）
      localStorage.removeItem('wx_open_id')
      try {
        sessionStorage.removeItem('wx_open_id')
      } catch (e) {
        // sessionStorage清除失败不影响流程
      }
    }
  },

  actions: {
    // 获取用户信息
    GetInfo ({ commit, state }) {
      return new Promise((resolve, reject) => {
        // 先尝试从localStorage获取
        const savedUserInfo = localStorage.getItem('userInfo')
        
        if (savedUserInfo) {
          try {
            const userInfo = JSON.parse(savedUserInfo)
            
            commit('SET_NAME', { 
              name: userInfo.nickname || userInfo.username || '用户',
              nickname: userInfo.nickname || userInfo.username || '用户'
            })
            commit('SET_AVATAR', userInfo.avatar || '')
            commit('SET_PHONE', userInfo.phone || '')
            // 从localStorage获取的余额可能不准确，优先使用当前余额，如果localStorage中有明确的值则使用
            const savedBalance = userInfo.balance !== undefined && userInfo.balance !== null ? userInfo.balance : (state.balance || 0)
            commit('SET_BALANCE', savedBalance)
            commit('SET_INFO', userInfo)
            resolve(userInfo)
            return
          } catch (e) {
            console.error('解析用户信息失败:', e)
          }
        }

        // 如果没有本地信息，调用API获取
        if (state.token) {
          getUserInfo().then(response => {
            const userData = response.data || response
            
            const userInfo = {
              nickname: userData.realName || userData.nickname || userData.name || userData.username || '用户',
              realName: userData.realName || '',
              username: userData.username || userData.account || '',
              phone: userData.phone || userData.mobile || '',
              balance: userData.balance || userData.amount || 0,
              avatar: userData.avatar || '',
              userId: userData.userId || userData.id || userData.uid || userData.uuid || null,
              wxOpenId: userData.wxOpenId || userData.openId || null
            }

            commit('SET_NAME', { 
              name: userInfo.nickname,
              nickname: userInfo.nickname,
              realName: userInfo.realName
            })
            commit('SET_AVATAR', userInfo.avatar)
            commit('SET_PHONE', userInfo.phone)
            commit('SET_BALANCE', userInfo.balance)
            commit('SET_INFO', userInfo)

            // 保存到localStorage
            localStorage.setItem('userInfo', JSON.stringify(userInfo))
            // 单独缓存 userId，便于页面切换场景快速读取
            if (userInfo && userInfo.userId) {
              localStorage.setItem('userId', String(userInfo.userId))
            }
            
            resolve(userInfo)
          }).catch(error => {
            console.error('获取用户信息失败:', error)
            // 获取用户信息失败时，保留当前余额，不设置为0
            const currentBalance = state.balance || 0
            const defaultUserInfo = {
              nickname: '用户',
              username: '',
              phone: '',
              balance: currentBalance, // 保留当前余额
              avatar: ''
            }
            commit('SET_NAME', { 
              name: defaultUserInfo.nickname,
              nickname: defaultUserInfo.nickname
            })
            // 不更新余额，保留当前值
            commit('SET_INFO', defaultUserInfo)
            resolve(defaultUserInfo)
          })
        } else {
          reject(new Error('No token available'))
        }
      })
    },

    // 登出
    Logout ({ commit }) {
      return new Promise((resolve) => {
        commit('CLEAR_USER')
        resolve()
      })
    },

    // 强制刷新用户信息（忽略缓存）
    ForceRefreshUserInfo ({ commit, state }) {
      return new Promise((resolve, reject) => {
        // 清除localStorage缓存
        localStorage.removeItem('userInfo')
        
        // 调用API获取最新用户信息
        if (state.token) {
          getUserInfo().then(response => {
            const userData = response.data || response
            const userInfo = {
              nickname: userData.realName || userData.nickname || userData.name || userData.username || '用户',
              realName: userData.realName || '',
              username: userData.username || userData.account || '',
              phone: userData.phone || userData.mobile || '',
              balance: userData.balance || userData.amount || 0,
              avatar: userData.avatar || '',
              userId: userData.userId || userData.id || userData.uid || userData.uuid || null,
              wxOpenId: userData.wxOpenId || userData.openId || null
            }

            commit('SET_NAME', { 
              name: userInfo.nickname,
              nickname: userInfo.nickname,
              realName: userInfo.realName
            })
            commit('SET_AVATAR', userInfo.avatar)
            commit('SET_PHONE', userInfo.phone)
            commit('SET_BALANCE', userInfo.balance)
            commit('SET_INFO', userInfo)

            // 保存到localStorage
            localStorage.setItem('userInfo', JSON.stringify(userInfo))
            // 单独缓存 userId
            if (userInfo && userInfo.userId) {
              localStorage.setItem('userId', String(userInfo.userId))
            }
            
            resolve(userInfo)
          }).catch(error => {
            console.error('强制刷新用户信息失败:', error)
            reject(error)
          })
        } else {
          reject(new Error('No token available'))
        }
      })
    },

    // 从营销后端获取当前用户对应商户余额
    async FetchMerchantBalance({ commit, state }) {
      // 保存当前余额，以便在失败时保留
      const currentBalance = state.balance || 0
      const currentGiftedBalance = state.giftedBalance || 0
      
      try {
        // 获取用户ID：优先从 state.info，其次从 localStorage
        let adminId = (state.info && (state.info.userId || state.info.id)) || null
        if (!adminId) {
          adminId = localStorage.getItem('userId')
        }
        if (!adminId) {
          // 从 token 解析
          try {
            const tk = state.token || localStorage.getItem('token')
            if (tk) {
              const parts = tk.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                adminId = payload.userId || payload.uid || payload.sub || null
              }
            }
          } catch (e) { /* ignore */ }
        }
        
        if (!adminId) {
          console.warn('FetchMerchantBalance: 无法获取用户ID，保留当前余额')
          // 无法获取用户ID时，保留当前余额和赠送余额，不设置为0
          // 确保赠送余额也被保留
          if (state.giftedBalance === undefined || state.giftedBalance === null) {
            commit('SET_GIFTED_BALANCE', currentGiftedBalance)
          }
          return currentBalance
        }
        
        // 优先调用"根据用户ID获取商户"专用接口
        const resp = await getBusinessByUser(adminId)
        
        // 处理响应数据结构：MyApiResponse { code: 200, data: BusinessDTO }
        let business = null
        if (resp) {
          if (resp.code === 200 && resp.data) {
            business = resp.data
          } else if (resp.data) {
            if (resp.data.businessId || resp.data.id) {
              business = resp.data
            } else if (resp.data.data && (resp.data.data.businessId || resp.data.data.id)) {
              business = resp.data.data
            }
          } else if (resp.businessId || resp.id) {
            business = resp
          }
        }

        if (business) {
          // 保存商户信息到 localStorage，供其他页面使用
          try {
            localStorage.setItem('currentBusiness', JSON.stringify(business))
          } catch (e) {
            console.warn('保存商户信息到 localStorage 失败:', e)
          }
          
          // 兼容多种字段：walletBalance / walletBalanceYuan / balance / amount
          let walletBalance = business.walletBalance
          if (walletBalance === undefined || walletBalance === null) walletBalance = business.walletBalanceYuan
          if (walletBalance === undefined || walletBalance === null) walletBalance = business.balance
          if (walletBalance === undefined || walletBalance === null) walletBalance = business.amount
          // 将字符串/BigDecimal转为Number展示（不影响精度来源于后端）
          const value = typeof walletBalance === 'string' ? parseFloat(walletBalance) : (walletBalance || 0)
          commit('SET_BALANCE', isNaN(value) ? 0 : value)
          
          // 获取赠送账户余额 - 兼容多种字段名
          let giftedAmount = business.giftedAmount
          const giftedValue = typeof giftedAmount === 'string' ? parseFloat(giftedAmount) : (giftedAmount || 0)
          commit('SET_GIFTED_BALANCE', isNaN(giftedValue) ? 0 : giftedValue)
          
          return isNaN(value) ? 0 : value
        }
        
        // 无法获取商户信息时，保留当前余额和赠送余额，不设置为0
        console.warn('FetchMerchantBalance: 无法获取商户信息，保留当前余额', {
          adminId,
          resp
        })
        // 确保赠送余额也被保留
        if (state.giftedBalance === undefined || state.giftedBalance === null) {
          commit('SET_GIFTED_BALANCE', currentGiftedBalance)
        }
        return currentBalance
      } catch (e) {
        console.error('获取商户余额失败:', e)

        // API调用失败时，先尝试从 UIC 获取用户余额作为兜底
        try {
          const resp3 = await getUserBalance()
          const val = resp3 && resp3.data != null ? resp3.data : (resp3 || 0)
          const num = typeof val === 'string' ? parseFloat(val) : val
          if (!isNaN(num) && num >= 0) {
            commit('SET_BALANCE', num)
            return num
          }
        } catch (e3) {
          console.error('UIC 用户余额兜底也失败:', e3)
        }
        
        // 如果UIC兜底也失败，保留当前余额和赠送余额，不设置为0
        console.warn('FetchMerchantBalance: 所有获取余额的方式都失败，保留当前余额:', currentBalance)
        // 确保赠送余额也被保留（如果之前没有被更新）
        if (state.giftedBalance === undefined || state.giftedBalance === null) {
          commit('SET_GIFTED_BALANCE', currentGiftedBalance)
        }
        return currentBalance
      }
    }
  },

  getters: {
    token: state => state.token,
    name: state => state.name,
    nickname: state => state.nickname,
    realName: state => state.realName,
    avatar: state => state.avatar,
    phone: state => state.phone,
    balance: state => state.balance,
    giftedBalance: state => state.giftedBalance,
    userInfo: state => state.info
  }
}

export default user
